import React, { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Images, Plus, Trash2, Sparkle, Palette, Cake, Loader2, MessageSquare } from "lucide-react";

const ACCENTS = ["#c9a24a", "#8a6cae", "#5a8c72", "#b06a8e", "#6a7fb0"];

const Boutique = () => {
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vibe, setVibe] = useState("");
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const res = await api.get("/boutique/me");
    const bt = res.data.boutique || {};
    setB({
      accent_color: bt.accent_color || "#c9a24a",
      heading_style: bt.heading_style || "playfair",
      layout: bt.layout || "masonry",
      inspiration: Array.isArray(bt.inspiration) ? bt.inspiration : [],
      vibes: Array.isArray(bt.vibes) ? bt.vibes : [],
      milestones: Array.isArray(bt.milestones) ? bt.milestones : [],
    });
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const toB64 = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.readAsDataURL(file); });

  const addInspiration = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const img = await toB64(file);
    const caption = window.prompt("Caption (optional)", "") || "";
    await api.post("/boutique/inspiration", { image_base64: img, caption });
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const removeInspiration = async (id) => { await api.delete(`/boutique/inspiration/${id}`); load(); };

  const postVibe = async () => {
    if (!vibe.trim()) return;
    setPosting(true);
    try { await api.post("/boutique/vibe", { type: "thought", content: vibe }); setVibe(""); load(); }
    finally { setPosting(false); }
  };

  const setAccent = async (c) => { await api.put("/boutique/me", { accent_color: c }); load(); };

  if (loading || !b) return <AppShell><div className="text-center py-24 text-muted-foreground">Opening your Boutique…</div></AppShell>;

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif text-4xl mb-1" style={{ color: b.accent_color }}>My Personal Boutique</h1>
          <p className="text-muted-foreground">Curate the inspirations & vibes that define your taste — the foundation for the Magic Wand.</p>
        </div>
        <div className="flex items-center gap-2" data-testid="accent-picker">
          <Palette className="w-4 h-4 text-muted-foreground" />
          {ACCENTS.map((c) => (
            <button key={c} data-testid="accent-swatch" onClick={() => setAccent(c)}
              className={`w-6 h-6 rounded-full border-2 ${b.accent_color === c ? "border-foreground scale-110" : "border-transparent"}`} style={{ background: c }} />
          ))}
        </div>
      </div>

      {/* Vibe composer */}
      <div className="surface rounded-[1.5rem] p-5 mb-8">
        <div className="flex items-center gap-2 mb-3"><Sparkle className="w-4 h-4" style={{ color: b.accent_color }} /><span className="font-medium">Share a Vibe</span></div>
        <div className="flex gap-2">
          <input data-testid="vibe-input" value={vibe} onChange={(e) => setVibe(e.target.value)}
            className="flex-1 bg-input border border-border rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-ring/40" placeholder="What are you loving lately?" />
          <button data-testid="post-vibe-btn" onClick={postVibe} disabled={posting}
            className="bg-gradient-to-r from-primary to-secondary text-white rounded-2xl px-5 hover:opacity-90 disabled:opacity-60 flex items-center gap-2">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />} Post
          </button>
        </div>
      </div>

      {/* Inspiration gallery */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-serif text-2xl flex items-center gap-2"><Images className="w-5 h-5" style={{ color: b.accent_color }} /> Inspiration Gallery</h2>
        <button data-testid="add-inspiration-btn" onClick={() => fileRef.current?.click()}
          className="flex items-center gap-2 surface rounded-2xl px-4 py-2 text-sm hover:border-primary/40"><Plus className="w-4 h-4" /> Add Image</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={addInspiration} data-testid="inspiration-input" />
      </div>
      {(b.inspiration || []).length === 0 ? (
        <div className="surface rounded-[1.5rem] p-12 text-center text-muted-foreground mb-10">Upload images and styles you love to build your taste profile.</div>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 mb-10" data-testid="inspiration-gallery">
          {(b.inspiration || []).map((it) => (
            <div key={it.id} className="mb-4 break-inside-avoid surface rounded-2xl overflow-hidden card-soft relative group">
              <img src={`data:image/png;base64,${it.image_base64}`} alt="" className="w-full object-cover" />
              {it.caption && <div className="p-3 text-sm text-muted-foreground italic">{it.caption}</div>}
              <button onClick={() => removeInspiration(it.id)} className="absolute top-2 right-2 bg-white/80 rounded-full p-1.5 opacity-0 group-hover:opacity-100 text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Milestones */}
      {b.milestones?.length > 0 && (
        <div>
          <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Cake className="w-5 h-5" style={{ color: b.accent_color }} /> My Milestones</h2>
          <div className="flex flex-wrap gap-3">
            {b.milestones.map((m, i) => (
              <div key={i} className="surface rounded-2xl px-4 py-3"><div className="font-medium">{m.label}</div><div className="text-xs text-muted-foreground">{m.date}</div></div>
            ))}
          </div>
        </div>
      )}
    </AppShell>
  );
};

export default Boutique;
